import React, { useEffect, useState } from 'react';
import { useBooking } from '../../context/BookingContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from '../../hooks/use-toast';
import { Clock, Calendar, IndianRupee, UserRound, Loader2, Gift } from 'lucide-react';
import { AddressAutocomplete } from './AddressAutocomplete';
import { CouponSection, couponDiscount } from './CouponSection';

const FALLBACK_RATES = [
  { hours: 1, price: 250 }, { hours: 2, price: 350 }, { hours: 3, price: 400 }, { hours: 4, price: 500 },
  { hours: 5, price: 600 }, { hours: 6, price: 700 }, { hours: 7, price: 800 }, { hours: 8, price: 900 },
];

const getIstHour = () => {
  const part = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', hour12: false })
    .formatToParts(new Date())
    .find((p) => p.type === 'hour');
  return part ? parseInt(part.value, 10) % 24 : new Date().getHours();
};

const BookingForm = ({ pickup, drop, onPickupChange, onDropChange, route, userCoords }) => {
  const { createBooking } = useBooking();
  const { user } = useAuth();
  const [config, setConfig] = useState({
    hourly_rates: FALLBACK_RATES, coupons: [], search_radius_km: 5,
    late_night_charge: 200, late_night_start_hour: 22, late_night_end_hour: 6,
  });
  const [pickupText, setPickupText] = useState('');
  const [dropText, setDropText] = useState('');
  const [hours, setHours] = useState(null);
  const [bookingTime, setBookingTime] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [femaleOnly, setFemaleOnly] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/api/config/pricing').then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const baseAmount = config.hourly_rates.find((r) => r.hours === hours)?.price || 0;
  const referralPercent = !coupon && user?.pendingDiscountPercent > 0 ? user.pendingDiscountPercent : 0;
  const discount = coupon ? couponDiscount(coupon, baseAmount) : Math.floor((baseAmount * referralPercent) / 100);
  const pickupHour = bookingTime === 'later'
    ? (scheduledTime ? parseInt(scheduledTime.split(':')[0], 10) : null)
    : getIstHour();
  const isLateNight = pickupHour != null
    && (pickupHour >= (config.late_night_start_hour ?? 22) || pickupHour < (config.late_night_end_hour ?? 6));
  const lateNightCharge = isLateNight ? (config.late_night_charge ?? 200) : 0;
  const total = Math.max(baseAmount - discount, 0) + lateNightCharge;

  const fail = (title, description) => toast({ title, description, variant: 'destructive' });

  const handleBooking = async () => {
    if (!pickup) return fail('Select Pickup Location', 'Please choose your pickup from the suggestions');
    if (!drop) return fail('Select Drop Location', 'Please choose your drop from the suggestions');
    if (!hours) return fail('Select Hours', 'Please select the number of hours');
    if (bookingTime === 'later' && (!scheduledDate || !scheduledTime)) {
      return fail('Schedule Time Required', 'Please select date and time for scheduled booking');
    }
    setSubmitting(true);
    try {
      await createBooking({
        hours,
        pickupLocation: pickup.full,
        dropLocation: drop.full,
        pickupLat: pickup.lat, pickupLng: pickup.lng,
        dropLat: drop.lat, dropLng: drop.lng,
        routeDistanceKm: route?.distance_km ?? null,
        routeDurationMin: route?.duration_min ?? null,
        femaleDriverOnly: femaleOnly,
        bookingTime,
        scheduledDate: bookingTime === 'later' ? scheduledDate : null,
        scheduledTime: bookingTime === 'later' ? scheduledTime : null,
        couponCode: coupon?.code || null,
      });
      toast({
        title: 'Booking Confirmed — Searching for a Driver',
        description: `Notifying drivers within ${config.search_radius_km} km of your pickup. Pay once a driver accepts.`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      fail('Booking Failed', typeof detail === 'string' ? detail : 'Could not create booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-6" data-testid="booking-form">
      <h1 className="text-2xl sm:text-3xl font-bold mb-1">Where do you need a driver?</h1>
      <p className="text-sm text-muted-foreground mb-6">A verified driver comes to you and drives your own car, billed by the hour.</p>

      <div className="mb-5">
        <Label className="text-base mb-3 block">When is Driver Needed?</Label>
        <Tabs value={bookingTime} onValueChange={setBookingTime}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="now" data-testid="booking-time-now"><Clock className="w-4 h-4 mr-2" />Now</TabsTrigger>
            <TabsTrigger value="later" data-testid="booking-time-later"><Calendar className="w-4 h-4 mr-2" />Schedule for Later</TabsTrigger>
          </TabsList>
          <TabsContent value="later" className="mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input id="date" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]} className="h-10" data-testid="booking-date-input" />
              </div>
              <div>
                <Label htmlFor="time">Time</Label>
                <Input id="time" type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)}
                  className="h-10" data-testid="booking-time-input" />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <div className="mb-4">
        <AddressAutocomplete id="pickup" label="Pickup Location" placeholder="Search pickup address"
          value={pickupText} place={pickup} userCoords={userCoords} bias={userCoords}
          onChange={(t) => { setPickupText(t); if (pickup) onPickupChange(null); }}
          onSelect={(p) => { setPickupText(p.full); onPickupChange(p); }}
          testId="booking-pickup-input" />
        <p className="text-xs text-muted-foreground mt-1" data-testid="pickup-geo-hint">
          Drivers within {config.search_radius_km} km of this pickup will be notified
        </p>
      </div>

      <div className="mb-6">
        <AddressAutocomplete id="drop" label="Drop Location" placeholder="Search drop address"
          value={dropText} place={drop} bias={pickup || userCoords} iconColor="text-green-500"
          onChange={(t) => { setDropText(t); if (drop) onDropChange(null); }}
          onSelect={(p) => { setDropText(p.full); onDropChange(p); }}
          testId="booking-drop-input" />
      </div>

      <div className="mb-6">
        <Label className="text-base mb-3 block">Number of Hours</Label>
        <div className="grid grid-cols-2 gap-3">
          {config.hourly_rates.map((rate) => (
            <button key={rate.hours} type="button" onClick={() => setHours(rate.hours)} data-testid={`hours-${rate.hours}`}
              className={`p-4 rounded-lg border-2 transition-all ${hours === rate.hours ? 'border-primary bg-muted' : 'border-border hover:border-border'}`}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold"><Clock className="w-5 h-5" />{rate.hours}hr</span>
                <span className="flex items-center text-lg font-bold"><IndianRupee className="w-4 h-4" />{rate.price}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6 flex items-center justify-between rounded-lg border border-pink-200 bg-pink-50/60 p-4">
        <div className="flex items-start gap-3">
          <UserRound className="w-5 h-5 text-pink-600 mt-0.5" />
          <div>
            <Label htmlFor="female-only" className="text-base">Female drivers only</Label>
            <p className="text-xs text-muted-foreground">Your request will be sent only to verified female drivers</p>
          </div>
        </div>
        <Switch id="female-only" checked={femaleOnly} onCheckedChange={setFemaleOnly} data-testid="female-driver-toggle" />
      </div>

      <div className="mb-6">
        <CouponSection coupons={config.coupons} baseAmount={baseAmount} applied={coupon}
          onApply={setCoupon} onRemove={() => setCoupon(null)} />
      </div>

      {referralPercent > 0 && !coupon && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4" data-testid="referral-discount-banner">
          <Gift className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">
            <span className="font-semibold">{referralPercent}% referral reward</span> will be applied automatically on this booking!
          </p>
        </div>
      )}

      {hours && (
        <div className="bg-muted p-4 rounded-lg mb-6 space-y-2" data-testid="price-summary">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Base Fare ({hours} Hour{hours > 1 ? 's' : ''})</span>
            <span>₹{baseAmount}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-sm text-green-600" data-testid="applied-discount-line">
              <span>Discount {coupon ? `(${coupon.code})` : '(Referral reward)'}</span><span>-₹{discount}</span>
            </div>
          )}
          {lateNightCharge > 0 && (
            <div className="flex justify-between text-sm text-amber-700" data-testid="late-night-charge-line">
              <span>Late Night Charge (10 PM–6 AM)</span><span>+₹{lateNightCharge}</span>
            </div>
          )}
          <div className="border-t pt-2 flex justify-between items-center">
            <span className="font-semibold">Total Amount</span>
            <span className="text-2xl font-bold" data-testid="total-amount">₹{total}</span>
          </div>
          <p className="text-xs text-muted-foreground">+ GST (18%) will be added to final invoice</p>
        </div>
      )}

      <Button size="lg" className="w-full bg-primary hover:bg-primary-dark text-lg" onClick={handleBooking}
        disabled={submitting} data-testid="booking-submit-btn">
        {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing…</> : 'Confirm Booking'}
      </Button>
    </Card>
  );
};

export default BookingForm;
